import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, Eye, Settings, ListFilter, AlertTriangle, Wrench, RefreshCw, Boxes, Download, KeyRound,
  Check, X, ArrowRightLeft, RotateCcw,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { truncateText } from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ASSET_REQUEST } from "../../constants/api";
import {
  ASSET_REQUEST_STATUSES, ASSET_REQUEST_TYPES,
  assetReqStatusClass, assetReqPriorityClass, assetReqTypeClass, shortType,
} from "../../constants/assetRequest";
import { fmtDate } from "./AssetRequestPieces";

/* Admin list of all employee asset requests. Type filters render as Leave-Request
   style tab buttons; status filters render as approval-inbox style chips — both
   inside the table card (passed via Reactable's children slot). */

// Issue-type tabs (Leave-Request button style). "All" + each static issue type.
const TYPE_ICON = {
  "New Asset Request": Boxes,
  "Incident / Problem": AlertTriangle,
  "Asset Replacement": RefreshCw,
  "Asset Repair": Wrench,
  "Software Installation": Download,
  "Access Request": KeyRound,
};
const TYPE_TABS = [
  { value: "All", label: "All", Icon: ListFilter },
  ...ASSET_REQUEST_TYPES.map((t) => ({ value: t, label: shortType(t), Icon: TYPE_ICON[t] || ListFilter })),
];

// Status chip palettes (active vs idle), mirroring the approval-inbox chip row.
const STATUS_CHIP = {
  All: { active: "bg-sidebar text-white border-transparent", idle: "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100" },
  Pending: { active: "bg-amber-100 text-amber-700 border-amber-300", idle: "bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100" },
  Approved: { active: "bg-violet-100 text-violet-700 border-violet-300", idle: "bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100" },
  Issued: { active: "bg-green-100 text-green-700 border-green-300", idle: "bg-green-50 text-green-600 border-green-200 hover:bg-green-100" },
  Rejected: { active: "bg-red-100 text-red-700 border-red-300", idle: "bg-red-50 text-red-600 border-red-200 hover:bg-red-100" },
  Cancelled: { active: "bg-slate-200 text-slate-700 border-slate-300", idle: "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100" },
  Returned: { active: "bg-sky-100 text-sky-700 border-sky-300", idle: "bg-sky-50 text-sky-600 border-sky-200 hover:bg-sky-100" },
};

const TabButton = ({ active, label, Icon, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
      active
        ? "bg-sidebar text-white dark:bg-sidebar-primary"
        : "text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700"
    )}
  >
    {Icon && <Icon className="h-4 w-4 sm:mr-2" />}
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const StatusChip = ({ status, active, count, onClick }) => {
  const pal = STATUS_CHIP[status] || STATUS_CHIP.All;
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors cursor-pointer",
        active ? `${pal.active} scale-105` : pal.idle
      )}
    >
      {status !== "All" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {status}
      <span className={cn(active ? "font-semibold" : "font-normal")}>({count})</span>
    </button>
  );
};

const AssetRequestList = () => {
  const navigate = useNavigate();
  const { update } = useApiMethods();
  const [requests, setRequests] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [status, setStatus] = useState("All");
  const [type, setType] = useState("All");
  const [serverTrigger, setServerTrigger] = useState(false);
  const [actingId, setActingId] = useState(null);

  // Asset-request admin actions are gated by the System Admin role (no separate
  // user-permission entry exists for this module).
  const editPermission = JSON.parse(sessionStorage.getItem("is_system_admin") || "false")
    || JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

  // One-click workflow action straight from the row.
  const act = async (row, payload, successMsg) => {
    setActingId(row.id);
    try {
      const res = await update(ASSET_REQUEST, { id: row.id, ...payload });
      if (res?.status === "success") {
        ToastMsg("success", successMsg || res.message);
        setServerTrigger((t) => !t);
      } else {
        ToastMsg("error", res?.message || "Action failed.");
      }
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setActingId(null);
    }
  };

  const filter = useMemo(() => {
    const f = {};
    if (status !== "All") f.status = status;
    if (type !== "All") f.request_type = type;
    return f;
  }, [status, type]);

  // Best-effort counts from the currently loaded rows. Once the backend returns
  // a status summary, swap these to use it.
  const counts = useMemo(() => {
    const c = { All: totalRecords || requests.length };
    ASSET_REQUEST_STATUSES.forEach((s) => { c[s] = requests.filter((r) => r.status === s).length; });
    return c;
  }, [requests, totalRecords]);

  const columns = [
    { accessor: "request_no", Header: "Request", filterable: true, sortable: true,
      Cell: ({ value }) => <span className="font-bold text-sidebar-primary">{value ?? "-"}</span> },
    { accessor: "requester_name", Header: "Requester", filterable: true,
      Cell: ({ value, row }) => (
        <span className="block min-w-0 max-w-[180px] sm:max-w-[220px]">
          <span className="block truncate text-sm font-medium" title={value ?? undefined}>{value ?? "-"}</span>
          <span className="block truncate text-xs text-muted-foreground" title={row?.department ?? undefined}>{row?.department ?? ""}</span>
        </span>
      ) },
    { accessor: "subject", Header: "Subject", filterable: true,
      Cell: ({ value }) => <span className="font-medium">{truncateText(value, 28)}</span> },
    { accessor: "request_type", Header: "Issue Type",
      Cell: ({ value }) => (
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", assetReqTypeClass(value))}>
          {shortType(value) || "-"}
        </span>
      ) },
    { accessor: "category_name", Header: "Category", Cell: ({ value }) => value ?? "-" },
    { accessor: "priority", Header: "Priority",
      Cell: ({ value }) => (
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", assetReqPriorityClass(value))}>
          {value ?? "-"}
        </span>
      ) },
    { accessor: "status", Header: "Status", filterable: true,
      Cell: ({ value }) => (
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", assetReqStatusClass(value))}>
          {value ?? "-"}
        </span>
      ) },
    { accessor: "justification", Header: "Justification",
      Cell: ({ value }) => truncateText(value, 20) },
    { accessor: "created_at", Header: "Submitted", sortable: true,
      Cell: ({ value }) => <span className="text-xs text-muted-foreground">{fmtDate(value)}</span> },
    { accessor: "Action", Header: "Action",
      Cell: ({ row }) => {
        const busy = actingId === row.id;
        const stop = (e) => e.stopPropagation();
        return (
          <div className="flex items-center gap-1" onClick={stop}>
            <Button variant="ghost" size="sm" className="text-sidebar-primary" title="View"
              onClick={() => navigate(`/assetmanagement/assetrequest/${row.id}`)}>
              <Eye className="h-4 w-4" />
            </Button>

            {editPermission && row.status === "Pending" && (
              <>
                <Button variant="ghost" size="sm" disabled={busy} title="Approve"
                  className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                  onClick={() => act(row, { status: "Approved", decision_note: "Approved" }, "Request approved.")}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" disabled={busy} title="Reject"
                  className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
                  onClick={() => act(row, { status: "Rejected", decision_note: "Rejected" }, "Request rejected.")}>
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}

            {editPermission && row.status === "Approved" && (
              <Button variant="ghost" size="sm" title="Issue asset"
                className="text-sidebar-primary hover:bg-sidebar-primary/10"
                onClick={() => navigate(`/assetmanagement/assetrequest/${row.id}`)}>
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
            )}

            {editPermission && row.status === "Issued" && (
              <Button variant="ghost" size="sm" disabled={busy} title="Mark returned"
                className="text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30"
                onClick={() => act(row, { status: "Returned" }, "Asset marked returned.")}>
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      } },
  ];

  return (
    <ContainerCard>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ModuleTitleCard
            Icon={Inbox}
            title="Asset Requests"
            subtitle="Review, approve and issue employee asset requests"
            totalRecords={totalRecords}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mr-3 shrink-0"
          onClick={() => navigate("/assetmanagement/settings")}
          title="Asset request settings"
        >
          <Settings className="h-4 w-4" /> Settings
        </Button>
      </div>

      <Reactable
        columns={columns}
        data={requests}
        setData={setRequests}
        setTotalCount={setTotalRecords}
        url={ASSET_REQUEST}
        filter={filter}
        serverSide
        serverTrigger={serverTrigger}
        isCSVRequired
        isPDFRequired
        rowSize="sm"
        onRowClick={(row) => navigate(`/assetmanagement/assetrequest/${row.id}`)}
      >
        {/* Toolbar (rendered inside the table card, above the export/search row) */}
        <div className="flex flex-col gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          {/* Type tabs — Leave-Request button style */}
          <div className="flex gap-2 overflow-x-auto mb-1 border-b border-gray-200 dark:border-gray-700 pb-2">
            {TYPE_TABS.map((t) => (
              <TabButton
                key={t.value}
                active={type === t.value}
                label={t.label}
                Icon={t.Icon}
                onClick={() => setType(t.value)}
              />
            ))}
          </div>
          {/* Status chips — approval-inbox style */}
          <div className="flex flex-wrap gap-2">
            {["All", ...ASSET_REQUEST_STATUSES].map((s) => (
              <StatusChip
                key={s}
                status={s}
                active={status === s}
                count={counts[s] ?? 0}
                onClick={() => setStatus(s)}
              />
            ))}
          </div>
        </div>
      </Reactable>
    </ContainerCard>
  );
};

export default AssetRequestList;
